import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Dialog, DialogType, Spinner, SpinnerSize, DayOfWeek } from "office-ui-fabric-react";
import { TimeTrackingPersistTimelineList } from "../../../timeTracking/TimeTrackingPersistTimelineList";
import { ApplicationState } from "../../../../store";
import { ApprovalRequest, ApprovalRequestStatus } from "../../../../store/ApprovalCenterStore";
import { actionCreators, TimesheetApprovalDetails, TimeTrackingEntriesState } from "../../../../store/TimesheetApprovalStore";
import { BuildEntriesTimeTrackingLines, BuildProjectLines, ControlSettings } from "../../../timeTracking/common";
import { ITimeframe } from "../../../../entities/common";
import { Views } from "../../../../store/services/viewSaver";
import "./TimeTrackingEntriesDialog.css";
import "./TimesheetRequestGrid.css";
import { FormatDate } from "../../../utils/common";
import { ApprovalStatusBadge } from "../../ApprovalStatusBadge";
import { getTimesheetLineName } from "./timesheetApprovalFields";

type OwnProps = {
    request: ApprovalRequest<TimesheetApprovalDetails>;
    onDismiss: () => void;
};

type StateProps = { timeEntries: TimeTrackingEntriesState };
type ActionProps = { loadTimeEntries: typeof actionCreators.loadTimeEntries; clearTimeEntries: typeof actionCreators.clearTimeEntries };
type Props = OwnProps & StateProps & ActionProps;

const TimeTrackingEntriesDialog = (props: Props) => {
    const { request, timeEntries, loadTimeEntries, clearTimeEntries, onDismiss } = props;
    const { isLoading, result } = timeEntries;

    const period = request.details?.period;
    const error = timeEntries.error ?? (period ? null : "Invalid approval request context");

    useEffect(() => {
        loadTimeEntries(request.id);
        return () => {
            clearTimeEntries();
        };
    }, [request.id]);

    const timeFrame = useMemo<ITimeframe | null>(
        () => period ? { start: period.startDate, end: period.endDate } : null,
        [period]);

    const { lines, projectLines } = useMemo(() => {
        if (!result || !timeFrame) {
            return { lines: [], projectLines: [] };
        }
        const entryLines = BuildEntriesTimeTrackingLines(result.entries, [], timeFrame, result.tasks, result.projects, null);
        return { lines: entryLines, projectLines: BuildProjectLines(entryLines) };
    }, [result, timeFrame]);

    const controlSettings: ControlSettings = {
        viewType: Views.Timeline,
        suggestTimeEntries: false,
        timeline: {
            timeframe: timeFrame || { start: new Date(), end: new Date() }
        }
    };

    const dialogTitle = (
        <div className="time-tracking-dialog-title">
            <ApprovalStatusBadge status={request.attributes.Status} />
            {period && (
                <span>
                    {FormatDate(period.startDate)} - {FormatDate(period.endDate)},
                </span>
            )}
            {getTimesheetLineName(request.details?.isAdministrative, request.details?.project)}
        </div>
    );

    return (
        <Dialog
            hidden={false}
            onDismiss={onDismiss}
            dialogContentProps={{
                type: DialogType.close,
                title: dialogTitle,
                showCloseButton: true
            }}
            modalProps={{
                isBlocking: false,
                className: "time-tracking-entries-dialog"
            }}
            minWidth={1000}>
            <div className="time-tracking-entries-content">
                {isLoading ? (
                    <div className="loading-spinner">
                        <Spinner size={SpinnerSize.large} label="Loading time entries..." />
                    </div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : timeFrame ? (
                    <div className="time-tracking-grid timesheet-request-grid">
                        <TimeTrackingPersistTimelineList
                            lines={lines}
                            projectLines={projectLines}
                            timeFrame={timeFrame}
                            isReadonly={true}
                            isPeriodOpenForReporting={true}
                            isLoading={false}
                            controlSettings={controlSettings}
                            firstDayOfWeek={DayOfWeek.Monday}
                        />
                    </div>
                ) : null}
            </div>
        </Dialog>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => ({
    timeEntries: state.timesheetApprovals.timeEntries
});

const mapDispatchToProps = (dispatch: Dispatch): ActionProps =>
    bindActionCreators({
        loadTimeEntries: actionCreators.loadTimeEntries,
        clearTimeEntries: actionCreators.clearTimeEntries
    }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(TimeTrackingEntriesDialog);
